import {
  Alert,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { formatPercent } from './helpers'
import { useCoverage } from './useProductCategoryData'
import './CoverageTab.css'

function CountTable({ title, rows }: { title: string; rows: { name: string; count: number }[] }) {
  return (
    <TableContainer component={Paper} variant="outlined" className="coverage-tab-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{title}</TableCell>
            <TableCell align="right">Lines</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.name}>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">{row.count.toLocaleString('en-US')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export function CoverageTab() {
  const { coverage, error } = useCoverage()

  if (error) return <Alert severity="error">{error}</Alert>
  if (!coverage) return <LinearProgress />

  const percent = coverage.totalLines ? (coverage.categorizedLines / coverage.totalLines) * 100 : 0
  const stats = [
    {
      label: 'Lines with a category',
      value: `${percent.toFixed(1)}%`,
      detail: `${coverage.categorizedLines} of ${coverage.totalLines}`,
    },
    {
      label: 'Average confidence',
      value: formatPercent(coverage.averageConfidence),
      detail: 'across categorized lines',
    },
    { label: 'Awaiting review', value: String(coverage.awaitingReview), detail: 'parts' },
    { label: 'AI classifications', value: String(coverage.aiClassificationsThisMonth), detail: 'this month' },
  ]

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2}>
        {stats.map(stat => (
          <Paper key={stat.label} className="coverage-tab-stat" variant="outlined">
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
            <Typography variant="h5">{stat.value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {stat.detail}
            </Typography>
          </Paper>
        ))}
      </Stack>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <CountTable title="Source" rows={coverage.bySource.map(row => ({ name: row.source, count: row.count }))} />
        <CountTable
          title="Category"
          rows={coverage.byCategory.map(row => ({ name: row.category, count: row.count }))}
        />
      </Stack>
    </Stack>
  )
}
